import classnames from 'classnames';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';

import { DbKey, WithDbId } from '../../../../../adl-gen/common/db';
import {
  CompetencyBarsSlideout,
  makeInternationalized,
  Question,
  QuizType,
  WorkRole
} from '../../../../../adl-gen/totalinteraction/futurefit/db';
import {
  Entry,
  RepositoryEntries,
  RepositoryEntrySet
} from '../../../../../adl-gen/totalinteraction/futurefit/frontend';
import {
  CreateQuizReq,
  makeCreateQuizReq,
  RepositoryPutEntry
} from '../../../../../adl-gen/totalinteraction/futurefit/requests';
import { LangCode, withI18n, WithI18nProps } from '../../../../../internationalization/i18n-hoc';
import { SelectBox, SelectOption } from '../../../../../ui/widgets/select/select';
import {
  FieldState,
  newNonEmptyTextField,
  newRegexTextField,
  newTextField
} from '../../../../../ui/widgets/text-input/text-field';
import * as FileUtility from '../../../../../util/file-repos-utility';
import {
  CheckItemObserver,
  I18nInput,
  I18nTextInputObserver,
  renderButtons,
  SelectObserver
} from '../../../common/editor';
import { ImageFileManager, SelectedFileSpec } from '../../image-file-manager/image-file-manager';

const styles = require('../../../common/editor.css');

interface QuizEditorProps extends WithI18nProps {
  workrole: WithDbId<WorkRole>;
  repository: RepositoryEntrySet;
  nonSkillQuestions: WithDbId<Question>[];
  onCreate(item: CreateQuizReq): Promise<void>;
  onUpload(req: RepositoryPutEntry): Promise<Entry>;
  onCancel(): void;
  role: 'super' | 'client-admin';
}

interface EditorState {
  title: I18nInput;
  imageUrl: FieldState<string>;
  lang: LangCode;
  includeIntroduction?: DbKey<Question>;
  includeConclusion?: DbKey<Question>;
  guidanceAvailable: boolean;
  recommendationsAvailable: boolean;
  showIndustryAverage: boolean;
  hideExpected: boolean;
  competencyBars: CompetencyBarsSlideout;
  expectedLabel: I18nInput;
  actualLabel: I18nInput;
  groupLabel: I18nInput;
  industryLabel: I18nInput;
  lockResults: boolean;
  quizType: QuizType;
}

/**
 * Component to maintain Clients.
 */
@observer
class QuizEditor0 extends React.Component<QuizEditorProps> {
  @observable fieldState: EditorState = {
    title: makeInternationalized({ en: newNonEmptyTextField() }),
    imageUrl: newRegexTextField(
      /// This is a URL in the database but we should allow for upload as well
      /// FIXME this regex is not the best, a better one would be /^(http[s]?:(\/){2})([^\s\/]+\/).*$/ but
      /// grouping () does not seem to work
      /^http[s]?:\/\/[^\s\/]+\/?.*$/,
      'Only valid URLs allowed'
    ),
    lang: this.props.currentLangCode(),

    guidanceAvailable: true,
    recommendationsAvailable: true,
    showIndustryAverage: true,
    hideExpected: false,

    competencyBars: CompetencyBarsSlideout.all,

    expectedLabel: makeInternationalized({ en: newTextField() }),
    actualLabel: makeInternationalized({ en: newTextField() }),
    groupLabel: makeInternationalized({ en: newTextField() }),
    industryLabel: makeInternationalized({ en: newTextField() }),

    lockResults: false,
    quizType: QuizType.quiz
  };

  /// New image (tile) URL
  @observable newTile?: SelectedFileSpec;

  @observable locked = false;

  @observable debug = false;

  /// ----- [ TEMP until S3 sync is done ] -----

  /// Can't use "file://../../../assets/images/skillseeker-logo.jpg" - "Not allowed to load local resource"
  readonly defaultTile =
    'https://com-hx-futurefit-uat-resources.s3-ap-southeast-2.amazonaws.com/data-images/assessments/tiles/QwTob33uAJBq+Futurefit+Sales+Engineer.png';

  constructor(props: Readonly<QuizEditorProps>) {
    super(props);
    this.fieldState.imageUrl.onChange(this.defaultTile);
    this.fieldState.title = props.initI18nEmpty(this.fieldState.title);
  }

  @action
  onTileUrlSelect = async (newFile: SelectedFileSpec) => {
    switch (newFile.kind) {
      // ignore "default"
      case 'default':
        return;
      // should not happen - no FileSelector
      case 'repositoryFile':
        this.fieldState.imageUrl.onChange(newFile.uri);
        break;
      // Not implemented
      case 'remoteFile':
        this.fieldState.imageUrl.onChange(newFile.uri);
        break;
      case 'upload': {
        // Leave to onSave to handle
        this.newTile = newFile;
      }
    }
  };

  getRepo = (): RepositoryEntries | undefined => {
    return FileUtility.getAssessmentsRepo(this.props.repository);
  };

  uploadToRepo = async (newFile: SelectedFileSpec): Promise<string | undefined> => {
    const result = await FileUtility.uploadRepoFile(newFile, this.props.onUpload);
    return result;
  };

  @action lockUpdates = () => (this.locked = true);

  @action
  changeLang = (opt: SelectOption) => {
    this.fieldState.lang = opt.value as LangCode;
  };

  @action
  onSetCompetencyBarsSlideoutOption = (opt: SelectOption) => {
    this.fieldState.competencyBars = this.stringToCompetencyBarsSlideout(opt.value as string);
  };

  @action
  onSetIntroductionQuestion = (opt: SelectOption) => {
    this.fieldState.includeIntroduction = opt.value as string;
  };

  @action
  onSetConclusionQuestion = (opt: SelectOption) => {
    this.fieldState.includeConclusion = opt.value as string;
  };

  @action
  onSetQuizTypeOption = (opt: SelectOption) => {
    this.fieldState.quizType = this.stringToQuizType(opt.value as string);
  };

  stringToCompetencyBarsSlideout = (opt: string): CompetencyBarsSlideout => {
    switch (opt) {
      case 'none':
        return CompetencyBarsSlideout.none;
      case 'noSkills':
        return CompetencyBarsSlideout.noSkills;
      default:
        return CompetencyBarsSlideout.all;
    }
  };

  competencyBarsSlideoutToString = (opt: CompetencyBarsSlideout): string => {
    switch (opt) {
      case CompetencyBarsSlideout.all:
        return 'all';
      case CompetencyBarsSlideout.noSkills:
        return 'noSkills';
      default:
        return 'none';
    }
  };

  selectedSlideoutOption = (opt: CompetencyBarsSlideout): SelectOption => {
    switch (opt) {
      case CompetencyBarsSlideout.all:
        return { label: 'All Skills', value: this.competencyBarsSlideoutToString(CompetencyBarsSlideout.all) };
      case CompetencyBarsSlideout.noSkills:
        return {
          label: 'Competency Only',
          value: this.competencyBarsSlideoutToString(CompetencyBarsSlideout.noSkills)
        };
      default:
        return { label: 'No Slideout Bars', value: this.competencyBarsSlideoutToString(CompetencyBarsSlideout.none) };
    }
  };

  selectedQuestion = (selected?: DbKey<Question>): SelectOption => {
    const { nonSkillQuestions } = this.props;
    if (selected) {
      const option = nonSkillQuestions.find(item => item.id === selected);
      if (option) {
        return { label: option.value.name, value: option.id };
      }
    }
    return { label: 'Select...', value: '' };
  };

  competencyBarsSlideoutOptions = (): SelectOption[] => {
    return [
      this.selectedSlideoutOption(CompetencyBarsSlideout.all),
      this.selectedSlideoutOption(CompetencyBarsSlideout.noSkills),
      this.selectedSlideoutOption(CompetencyBarsSlideout.none)
    ];
  };

  questionOptions = (cat: string): SelectOption[] => {
    const { nonSkillQuestions } = this.props;
    return nonSkillQuestions
      .filter(item => item.value.category.kind === cat)
      .map(item => ({ label: item.value.name, value: item.id }));
  };

  stringToQuizType = (opt: string): QuizType => {
    switch (opt) {
      case 'quiz':
        return QuizType.quiz;
      case 'self-assessment':
        return QuizType.selfAssessment;
      default:
        return QuizType.quiz;
    }
  };

  quizTypeToString = (opt: QuizType): string => {
    switch (opt) {
      case QuizType.quiz:
        return 'quiz';
      case QuizType.selfAssessment:
        return 'self-assessment';
    }
  };

  selectedQuizTypeOption = (opt: QuizType): SelectOption => {
    switch (opt) {
      case QuizType.quiz:
        return { label: 'Quiz', value: this.quizTypeToString(QuizType.quiz) };
      case QuizType.selfAssessment:
        return { label: 'Self-Assessment', value: this.quizTypeToString(QuizType.selfAssessment) };
    }
  };

  quizTypeOptions = (): SelectOption[] => {
    return [this.selectedQuizTypeOption(QuizType.quiz), this.selectedQuizTypeOption(QuizType.selfAssessment)];
  };

  LangSelector = observer(() => {
    const { i18nOptions } = this.props;
    const onChange = this.changeLang;
    const options = i18nOptions();
    return (
      <SelectBox
        options={options}
        defaultValue={options.find(option => (option.value as LangCode) === this.fieldState.lang)}
        onChange={onChange}
        title={'Language'}
        xsSpan={2}
      />
    );
  });

  QuizTypeObserver = observer(() =>
    SelectObserver({
      options: this.quizTypeOptions(),
      selectedOption: this.selectedQuizTypeOption(this.fieldState.quizType),
      title: 'Quiz or Self-Assessment',
      xsSpan: 2,
      onChange: this.onSetQuizTypeOption,
      debug: this.debug
    })
  );

  TitleObserver = observer(() =>
    I18nTextInputObserver({
      field: this.fieldState.title,
      type: 'text',
      lang: this.fieldState.lang,
      title: 'Name of Quiz',
      placeholder: 'Quiz Name',
      xsSpan: 2,
      debug: this.debug
    })
  );

  TileUrlObserver = observer(() => {
    const tileUrl =
      this.fieldState.imageUrl.getValid() !== undefined ? this.fieldState.imageUrl.value() : this.defaultTile;
    return (
      <ImageFileManager
        title={'Quiz Tile URL'}
        repositoryEntries={this.getRepo()}
        defaultUrl={tileUrl}
        onChange={this.onTileUrlSelect}
        hideFileSelector={true}
      />
    );
  });

  GuidanceAvailableObserver = observer(() =>
    CheckItemObserver({
      field: this.fieldState.guidanceAvailable,
      label: 'Guidance Available',
      xsSpan: 2,
      onChange: this.toggleGuidanceAvailable,
      debug: this.debug
    })
  );

  RecommendationsAvailableObserver = observer(() =>
    CheckItemObserver({
      field: this.fieldState.recommendationsAvailable,
      label: 'Recommendations Available',
      xsSpan: 2,
      onChange: this.toggleRecommendationsAvailable,
      debug: this.debug
    })
  );

  ShowIndustryAverageObserver = observer(() =>
    CheckItemObserver({
      field: this.fieldState.showIndustryAverage,
      label: 'Display Industry Average',
      xsSpan: 2,
      onChange: this.toggleShowIndustryAverage,
      debug: this.debug
    })
  );

  HideExpectedObserver = observer(() =>
    CheckItemObserver({
      field: this.fieldState.hideExpected,
      label: 'Hide Expected/Desired',
      xsSpan: 2,
      onChange: this.toggleHideExpected,
      debug: this.debug
    })
  );

  LockResultsObserver = observer(() =>
    CheckItemObserver({
      field: this.fieldState.lockResults,
      label: 'Lock Results',
      xsSpan: 2,
      onChange: this.toggleLockResults,
      debug: this.debug
    })
  );

  CompetencyBarsSlideoutObserver = observer(() =>
    SelectObserver({
      options: this.competencyBarsSlideoutOptions(),
      selectedOption: this.selectedSlideoutOption(this.fieldState.competencyBars),
      title: 'Show Competency Bars',
      xsSpan: 2,
      onChange: this.onSetCompetencyBarsSlideoutOption,
      debug: this.debug
    })
  );

  IntroductionObserver = observer(() =>
    SelectObserver({
      options: this.questionOptions('introduction'),
      selectedOption: this.selectedQuestion(this.fieldState.includeIntroduction),
      title: 'Include Introduction step',
      xsSpan: 2,
      onChange: this.onSetIntroductionQuestion,
      debug: this.debug
    })
  );

  ConclusionObserver = observer(() =>
    SelectObserver({
      options: this.questionOptions('conclusion'),
      selectedOption: this.selectedQuestion(this.fieldState.includeConclusion),
      title: 'Include Ending/Conclusion step',
      xsSpan: 2,
      onChange: this.onSetConclusionQuestion,
      debug: this.debug
    })
  );

  ExpectedLabelObserver = observer(() =>
    I18nTextInputObserver({
      field: this.fieldState.expectedLabel,
      type: 'text',
      lang: this.fieldState.lang,
      title: "Alternative label for 'Desired for Role'",
      placeholder: '(Optional)',
      xsSpan: 2,
      debug: this.debug
    })
  );

  ActualLabelObserver = observer(() =>
    I18nTextInputObserver({
      field: this.fieldState.actualLabel,
      type: 'text',
      lang: this.fieldState.lang,
      title: "Alternative label for 'Current Results'",
      placeholder: '(Optional)',
      xsSpan: 2,
      debug: this.debug
    })
  );

  GroupLabelObserver = observer(() =>
    I18nTextInputObserver({
      field: this.fieldState.groupLabel,
      type: 'text',
      lang: this.fieldState.lang,
      title: "Alternative label for 'Group Average'",
      placeholder: '(Optional)',
      xsSpan: 2,
      debug: this.debug
    })
  );

  IndustryLabelObserver = observer(() =>
    I18nTextInputObserver({
      field: this.fieldState.industryLabel,
      type: 'text',
      lang: this.fieldState.lang,
      title: "Alternative label for 'Industry Average'",
      placeholder: '(Optional)',
      xsSpan: 2,
      debug: this.debug
    })
  );

  @action
  toggleGuidanceAvailable = () => (this.fieldState.guidanceAvailable = !this.fieldState.guidanceAvailable);

  @action
  toggleRecommendationsAvailable = () =>
    (this.fieldState.recommendationsAvailable = !this.fieldState.recommendationsAvailable);

  @action
  toggleShowIndustryAverage = () => (this.fieldState.showIndustryAverage = !this.fieldState.showIndustryAverage);

  @action
  toggleHideExpected = () => (this.fieldState.hideExpected = !this.fieldState.hideExpected);

  @action
  toggleLockResults = () => (this.fieldState.lockResults = !this.fieldState.lockResults);

  @computed
  get inputIsValid(): boolean {
    const { isI18nValid } = this.props;
    return (
      isI18nValid(this.fieldState.title) &&
      isI18nValid(this.fieldState.expectedLabel, true) &&
      isI18nValid(this.fieldState.actualLabel, true) &&
      isI18nValid(this.fieldState.groupLabel, true) &&
      isI18nValid(this.fieldState.industryLabel, true) &&
      this.fieldState.imageUrl.getValid() !== undefined
    );
  }

  onSave = async () => {
    const { isI18nNonEmpty, makeI18nContent, onCreate, workrole: role } = this.props;

    // Look for uploaded files
    if (this.newTile) {
      const newTileUrl = await this.uploadToRepo(this.newTile);
      if (newTileUrl) {
        this.fieldState.imageUrl.onChange(newTileUrl);
      }
    }
    /// Build from scratch here to get latest changes
    /// FF2-429 Add alternative labels for A/J/M - but *ONLY* if they are all set (throw away if not all set at once)
    const q = makeCreateQuizReq({
      workRoleId: role.id,
      imageUrl: this.fieldState.imageUrl.value(),
      title: makeI18nContent(this.fieldState.title),
      guidanceAvailable: this.fieldState.guidanceAvailable,
      recommendationsAvailable: this.fieldState.recommendationsAvailable,
      showIndustryAverage: this.fieldState.showIndustryAverage,
      hideExpected: this.fieldState.hideExpected,
      competencyBars: this.fieldState.competencyBars,
      introduction: this.fieldState.includeIntroduction || null,
      conclusion: this.fieldState.includeConclusion || null,
      expectedLabel: isI18nNonEmpty(this.fieldState.expectedLabel)
        ? makeI18nContent(this.fieldState.expectedLabel)
        : null,
      actualLabel: isI18nNonEmpty(this.fieldState.actualLabel) ? makeI18nContent(this.fieldState.actualLabel) : null,
      groupLabel: isI18nNonEmpty(this.fieldState.groupLabel) ? makeI18nContent(this.fieldState.groupLabel) : null,
      industryLabel: isI18nNonEmpty(this.fieldState.industryLabel)
        ? makeI18nContent(this.fieldState.industryLabel)
        : null,
      lockResults: this.fieldState.lockResults,
      quizType: this.fieldState.quizType
    });

    /// Discard this here
    // const newQuizId =
    await onCreate(q);
  };

  Edit = (title: string) => (
    <React.Fragment>
      <h4>{title}</h4>
      <div className={styles.row}>
        <this.LangSelector />
      </div>
      <div className={styles.row}>
        <this.TitleObserver />
      </div>
      <div className={styles.row}>
        <this.TileUrlObserver />
      </div>
      <div className={styles.row}>
        <this.QuizTypeObserver />
      </div>
      <div className={styles.row}>
        <this.IntroductionObserver />
        <this.ConclusionObserver />
      </div>
      <div className={classnames(styles.row, styles.checkItemSet)}>
        <this.GuidanceAvailableObserver />
        <this.RecommendationsAvailableObserver />
        <this.ShowIndustryAverageObserver />
        <this.HideExpectedObserver />
        <this.LockResultsObserver />
      </div>
      <div className={styles.row}>
        <this.CompetencyBarsSlideoutObserver />
      </div>
      <div>
        <this.ExpectedLabelObserver />
        <this.ActualLabelObserver />
        <this.GroupLabelObserver />
        <this.IndustryLabelObserver />
      </div>
    </React.Fragment>
  );

  render() {
    const { onCancel, workrole: role, t } = this.props;
    return (
      // {/* TODO aria (or DOM) labels */ }
      <div className={styles.page}>
        <div className={styles.body}>
          <div>
            {renderButtons(
              this.onSave,
              undefined,
              onCancel,
              () => this.inputIsValid && !this.locked,
              undefined,
              () => !this.locked
            )}
          </div>
          {this.Edit(`Generate Quiz for ${t(role.value.name)}`)}
        </div>
      </div>
    );
  }
}

export const QuizEditor = withI18n(QuizEditor0);



// WEBPACK FOOTER //
// ./src/admin/ui/components/editors/quiz-editor/quiz-editor.tsx